/* eslint-disable react/no-array-index-key */
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import { useEffect, useState, Fragment } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { cloneDeep } from 'lodash';
import { useGetLicenseDetailsQuery } from '../services/products.endpoints';
import { SupportedHostApps } from '../schemas/products.schemas';

interface AssignSupportedHostAppsProps {
  category: string;
  supportedHostApps: SupportedHostApps; // ensure each row supports priceOverride?: number | ''
  changeSupportedHostApps: (supportedHostApps: SupportedHostApps) => void;
}

const AssignSupportedHostApps = ({
  category,
  supportedHostApps,
  changeSupportedHostApps,
}: AssignSupportedHostAppsProps) => {
  const { data: details, isLoading } = useGetLicenseDetailsQuery();
  const [basename, setBasename] = useState('');
  const [version, setVersion] = useState('');

  useEffect(() => {
    if (supportedHostApps.length > 0 && details) {
      setVersion(supportedHostApps[0].version);
      const newBasename = details.find(({ basename: bname }) =>
        supportedHostApps[0].id.includes(`${bname}.`),
      );
      if (newBasename) {
        setBasename(newBasename.basename);
      }
    }
  }, [details, supportedHostApps]);

  if (!details) {
    return <CircularProgress />;
  }
  let basenameOptions = details.map((base) => base.basename);
  const blacklist = [
    'com.digitalanarchy.BackdropDesigner',
    'com.digitalanarchy.TextureAnarchy',
    'com.digitalanarchy.Primatte',
    'com.digitalanarchy.ToonIt',
  ];

  const bundleBasenames = [
    'com.digitalanarchy.BeautyBoxExtreme',
    'com.digitalanarchy.BeautyFlicker',
    'com.digitalanarchy.AnarchyComplete',
  ];

  const allowedSubscriptionBasenames = [
    'com.digitalanarchy.Anarchy',
    'com.digitalanarchy.AnarchyAllAccess',
  ];

  const photoPluginBasenames = ['com.digitalanarchy.BeautyBox'];

  const videoPluginBasenames = details
    .map((d) => d.basename)
    .filter(
      (b) => !bundleBasenames.includes(b) && !photoPluginBasenames.includes(b),
    );

  // Apply filtering based on category
  switch (category) {
    case 'Subscription':
      basenameOptions = basenameOptions.filter((name) =>
        allowedSubscriptionBasenames.includes(name),
      );
      break;
    case 'Video Plugin':
      basenameOptions = basenameOptions.filter(
        (name) =>
          !blacklist.includes(name) &&
          !bundleBasenames.includes(name) &&
          !allowedSubscriptionBasenames.includes(name),
      );
      break;
    case 'Bundle':
      basenameOptions = basenameOptions.filter((name) =>
        bundleBasenames.includes(name),
      );
      break;
    case 'Upgrade':
      basenameOptions = basenameOptions.filter(
        (name) =>
          !bundleBasenames.includes(name) &&
          !allowedSubscriptionBasenames.includes(name) &&
          !blacklist.includes(name),
      );
      break;
    case 'Photo Plugin':
      basenameOptions = basenameOptions.filter((name) =>
        photoPluginBasenames.includes(name),
      );
      break;
    default:
      // No filter
      break;
  }
  const { versions, ids } =
    details.find((base) => base.basename === basename) ?? {};

  return (
    <>
      <Grid item xs={6}>
        <TextField
          id="basename"
          label="Base Name in License Database"
          name="basename"
          required
          fullWidth
          select
          disabled={isLoading}
          value={basename}
          onChange={(e) => setBasename(e.target.value)}
        >
          {basenameOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {versions && (
        <Grid item xs={6}>
          <TextField
            id="version"
            label="Version"
            name="version"
            required
            fullWidth
            select
            disabled={isLoading}
            value={version}
            onChange={(e) => {
              setVersion(e.target.value);
              const apps = cloneDeep(supportedHostApps);
              changeSupportedHostApps(
                apps.map((app) => ({
                  ...app,
                  version: e.target.value,
                })),
              );
            }}
          >
            {versions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="h5">Supported Host Apps</Typography>
      </Grid>
      {supportedHostApps.map(
        ({ displayName, id, priceOverride, downloadPage }, i) => (
          <Fragment key={`${id || 'row'}-${i}`}>
            <Grid item xs={12} md={5}>
              {ids && (
                <TextField
                  id={`id-${i}`}
                  label="ID in License Database"
                  name="id"
                  required
                  fullWidth
                  select
                  disabled={isLoading}
                  value={id}
                  onChange={(e) => {
                    const apps = cloneDeep(supportedHostApps);
                    apps[i].id = e.target.value;
                    changeSupportedHostApps(apps);
                  }}
                >
                  {ids.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id={`displayName-${i}`}
                label="Display Name of Host App"
                name="displayName"
                required
                fullWidth
                placeholder="Avid"
                disabled={isLoading}
                value={displayName}
                onChange={(e) => {
                  const apps = cloneDeep(supportedHostApps);
                  apps[i].displayName = e.target.value;
                  changeSupportedHostApps(apps);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id={`priceOverride-${i}`}
                label="Added Cost ($)"
                name="priceOverride"
                type="number"
                fullWidth
                disabled={isLoading}
                value={priceOverride ?? ''}
                onChange={(e) => {
                  const apps = cloneDeep(supportedHostApps);
                  // blank = no override
                  if (e.target.value === '') {
                    apps[i].priceOverride = 0;
                  } else {
                    const num = Number(e.target.value);
                    if (!Number.isNaN(num)) {
                      apps[i].priceOverride = num;
                    }
                  }
                  changeSupportedHostApps(apps);
                }}
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText="Leave blank to use Full Price"
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <IconButton
                onClick={() => {
                  const apps = cloneDeep(supportedHostApps);
                  apps.splice(i, 1);
                  changeSupportedHostApps(apps);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id={`downloadPage-${i}`}
                label="Download Page for this Host"
                name="downloadPage"
                fullWidth
                disabled={isLoading}
                value={downloadPage ?? ''}
                placeholder="https://digitalanarchy.com/downloads/..."
                onChange={(e) => {
                  const apps = cloneDeep(supportedHostApps);
                  apps[i].downloadPage = e.target.value;
                  changeSupportedHostApps(apps);
                }}
                helperText="Leave blank to use the product Download Page"
              />
            </Grid>
          </Fragment>
        ),
      )}
      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => {
            const apps = cloneDeep(supportedHostApps);
            changeSupportedHostApps([
              ...apps,
              {
                displayName: '',
                id: '',
                version,
                priceOverride: 0,
                downloadPage: '',
              },
            ]);
          }}
        >
          Add a Host App
        </Button>
      </Grid>
    </>
  );
};

export default AssignSupportedHostApps;
